import { useEffect, useMemo } from 'react';
import { Play, Loader2, Wand2, FormInput, Braces } from 'lucide-react';
import {
  fireRequest,
  type EndpointInfo,
  type OpenApiParameter,
  type OpenApiSchema,
} from '@/api/client';
import {
  useTryIt,
  setEndpoint,
  setPathParam,
  setQueryParam,
  setHeaderParam,
  setBody,
  setBodyFields,
  setBodyMode,
  setCustomBodyFields,
  setLoading,
  setResult,
  setError,
  type CustomBodyField,
} from '@/store/tryIt';
import { recordRequest } from '@/store/history';
import { useSession } from '@/lib/session';
import { BodyFieldsForm } from './BodyFieldsForm';
import { cn } from '@/lib/cn';

/**
 * Try-It form. Two body editing modes:
 *
 *   - **Form** (default) — render satu input per property dari schema,
 *     type-aware (string text, enum select, boolean checkbox, dst). Auto-fill
 *     field auth (`secret_key`/`api_key`) dari session sehingga user gak
 *     ketik dua kali.
 *
 *   - **Raw JSON** — untuk power user yang mau ngirim body custom (mis. nested
 *     struct yang gak ter-cover form), kasih textarea biasa.
 *
 * Toggle pakai 2 button di header body section. On submit:
 *   - Mode 'form'  → serialize bodyFields ke JSON, drop field kosong yang
 *                    bukan required (biar gak kirim noise field default).
 *   - Mode 'raw'   → parse state.body string sebagai JSON.
 */

/** Field-name yang otomatis di-fill dari session JWT. */
const AUTH_FIELD_NAMES = ['secret_key', 'api_key', 'apiKey', 'secretKey'];

export function TryItForm({ endpoint }: { endpoint: EndpointInfo }) {
  const state = useTryIt();
  const session = useSession();
  const params = endpoint.operation.parameters || [];
  const pathParams = params.filter((p) => p.in === 'path');
  const queryParams = params.filter((p) => p.in === 'query');
  const headerParams = params.filter((p) => p.in === 'header');
  const hasBody = ['POST', 'PUT', 'PATCH'].includes(endpoint.methodUpper);

  const bodySchema: OpenApiSchema | null = useMemo(() => {
    const rb = endpoint.operation.requestBody;
    return rb?.content?.['application/json']?.schema ?? null;
  }, [endpoint.operationId]);

  const hasSchemaProperties = Boolean(
    bodySchema?.properties && Object.keys(bodySchema.properties).length > 0
  );

  const requiredFields = bodySchema?.required || [];

  /**
   * Saat endpoint berubah: reset body editor state.
   *   - Form mode: init bodyFields dari example + override field auth dari session.
   *   - Raw mode:  init body string dari example (JSON terformat).
   * Effect ini sengaja DEPENDS PADA operationId aja — kita gak mau timpa input
   * tiap kali session re-render.
   */
  useEffect(() => {
    setEndpoint(endpoint);
    // setEndpoint resets state to initial → customBodyFields jadi empty. Bagus.
    if (hasBody && hasSchemaProperties) {
      const skeleton = generateExampleFromSchema(bodySchema!) as Record<string, unknown>;
      // Auto-fill field auth dari session JWT (kalau session udah authenticated)
      if (session.authenticated && session.user) {
        for (const authField of AUTH_FIELD_NAMES) {
          if (authField in skeleton && session.user.secret_key) {
            skeleton[authField] = session.user.secret_key;
          }
        }
      }
      setBodyFields(skeleton);
      setBody(JSON.stringify(skeleton, null, 2));
    } else if (hasBody) {
      // Schema undocumented → kosongin fields, biar user nambah row via UI.
      setBodyFields({});
      setBody('');
      // Kalau user authenticated dan endpoint dibawah /api (kemungkinan
      // butuh secret_key), prefill 1 row secret_key biar gak perlu nambah
      // manual. Kalau salah, user bisa hapus row.
      if (session.authenticated && session.user?.secret_key) {
        setCustomBodyFields([
          {
            id: `cf_seed_${Date.now()}`,
            name: 'secret_key',
            value: session.user.secret_key,
          },
        ]);
      } else {
        setCustomBodyFields([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint.operationId, session.authenticated]);

  /** Field-name yang harus di-lock di Form mode (auto-fill dari session). */
  const lockedFields = useMemo(() => {
    if (!session.authenticated || !hasSchemaProperties) return [];
    const propNames = Object.keys(bodySchema?.properties || {});
    return propNames.filter((n) => AUTH_FIELD_NAMES.includes(n));
  }, [session.authenticated, bodySchema, hasSchemaProperties]);

  const handleSend = async () => {
    setLoading(true);
    setError(null);

    // Validate required path params
    for (const p of pathParams) {
      if (p.required && !state.pathParams[p.name]) {
        setError(`Missing required path parameter: ${p.name}`);
        return;
      }
    }

    // Resolve body berdasarkan mode aktif
    let parsedBody: unknown = undefined;
    if (hasBody) {
      if (state.bodyMode === 'form') {
        parsedBody = serializeBodyFields(state.bodyFields, bodySchema, state.customBodyFields);
        // Form mode: validate required fields punya value
        for (const r of requiredFields) {
          const v = (parsedBody as Record<string, unknown>)?.[r];
          if (v === undefined || v === null || v === '') {
            setError(`Field "${r}" wajib di-isi.`);
            return;
          }
        }
      } else {
        // Raw mode
        if (state.body.trim()) {
          try {
            parsedBody = JSON.parse(state.body);
          } catch (e) {
            setError(`JSON tidak valid: ${(e as Error).message}`);
            return;
          }
        }
      }
    }

    try {
      const result = await fireRequest(endpoint, {
        path: state.pathParams,
        query: state.queryParams,
        headers: state.headerParams,
        body: parsedBody,
      });
      setResult(result);
      recordRequest({
        operationId: endpoint.operationId,
        method: endpoint.methodUpper,
        path: endpoint.path,
        status: result.status,
        ok: result.ok,
        durationMs: result.durationMs,
      });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleResetExample = () => {
    if (!bodySchema) return;
    const skeleton = generateExampleFromSchema(bodySchema) as Record<string, unknown>;
    if (session.authenticated && session.user) {
      for (const authField of AUTH_FIELD_NAMES) {
        if (authField in skeleton && session.user.secret_key) {
          skeleton[authField] = session.user.secret_key;
        }
      }
    }
    setBodyFields(skeleton);
    setBody(JSON.stringify(skeleton, null, 2));
  };

  /** Switch mode + sync representasi: form→raw serialize, raw→form parse best-effort. */
  const handleModeChange = (next: 'form' | 'raw') => {
    if (next === state.bodyMode) return;
    if (next === 'raw') {
      // Form → Raw: serialize current fields ke JSON text
      const serialized = serializeBodyFields(state.bodyFields, bodySchema, state.customBodyFields);
      setBody(JSON.stringify(serialized, null, 2));
    } else {
      // Raw → Form: parse text → fields. Kalau JSON invalid, biarin fields apa adanya.
      try {
        const parsed = state.body.trim() ? JSON.parse(state.body) : {};
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          setBodyFields(parsed as Record<string, unknown>);
        }
      } catch {
        // ignore — fields stay as-is
      }
    }
    setBodyMode(next);
  };

  return (
    <section className="mt-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-extrabold text-lg">Try it out</h2>
        <button
          type="button"
          onClick={handleSend}
          disabled={state.isLoading}
          className={cn(
            'inline-flex items-center gap-2 px-5 h-11 rounded-pill border-[2.5px] border-border-strong',
            'bg-accent shadow-brutal hover:shadow-brutal-lg hover:-translate-y-0.5',
            'active:translate-y-0.5 active:shadow-brutal-sm transition-all',
            'font-display font-bold text-sm',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          {state.isLoading ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Play size={14} />
              Send
            </>
          )}
        </button>
      </div>

      {state.error && (
        <div className="mb-3 px-4 py-3 rounded-brutal-sm border-[2.5px] border-border-strong bg-method-delete shadow-brutal-sm text-sm font-bold">
          {state.error}
        </div>
      )}

      <div className="rounded-brutal border-[2.5px] border-border-strong bg-elevated shadow-brutal-sm divide-y-2 divide-border-strong overflow-hidden">
        <ParamGroup
          title="Path"
          params={pathParams}
          values={state.pathParams}
          onChange={setPathParam}
        />
        <ParamGroup
          title="Query"
          params={queryParams}
          values={state.queryParams}
          onChange={setQueryParam}
        />
        <ParamGroup
          title="Headers"
          params={headerParams}
          values={state.headerParams}
          onChange={setHeaderParam}
        />
        {hasBody && (
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-2 gap-2">
              <h3 className="text-2xs font-semibold uppercase tracking-wider text-fg-tertiary">
                Body
              </h3>
              <div className="flex items-center gap-2">
                {/* Mode toggle selalu muncul — Form mode works untuk schema-defined
                    DAN dynamic key/value. JSON mode untuk power user / nested struct. */}
                <div className="inline-flex rounded-pill border-2 border-border-strong bg-base p-0.5 shadow-brutal-sm">
                  <ModeButton
                    active={state.bodyMode === 'form'}
                    onClick={() => handleModeChange('form')}
                    icon={<FormInput size={12} />}
                    label="Form"
                  />
                  <ModeButton
                    active={state.bodyMode === 'raw'}
                    onClick={() => handleModeChange('raw')}
                    icon={<Braces size={12} />}
                    label="JSON"
                  />
                </div>
                {hasSchemaProperties && (
                  <button
                    type="button"
                    onClick={handleResetExample}
                    className="inline-flex items-center gap-1 px-3 h-7 rounded-pill border-2 border-border-strong bg-base shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-brutal-none transition-all text-2xs font-bold"
                    title="Reset isi body ke contoh default"
                  >
                    <Wand2 size={11} />
                    Reset
                  </button>
                )}
              </div>
            </div>

            {/* Render editor sesuai mode. Form mode dipake SEMUA endpoint —
                kalau schema empty, BodyFieldsForm tampilin dynamic
                key/value rows + "Add field" button (konsisten UX, gak
                ada jump ke raw JSON textarea kecuali user toggle JSON). */}
            {state.bodyMode === 'form' ? (
              <BodyFieldsForm
                schema={bodySchema}
                values={state.bodyFields}
                lockedFields={lockedFields}
                allowCustomFields={true}
              />
            ) : (
              <textarea
                value={state.body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={'{\n  "field": "value"\n}'}
                spellCheck={false}
                rows={hasSchemaProperties ? 8 : 6}
                className="w-full px-3 py-2 rounded-brutal-sm border-2 border-border-strong bg-base font-mono text-2xs text-fg placeholder:text-fg-tertiary focus:outline-none focus:shadow-brutal-sm transition-shadow resize-y"
              />
            )}
          </div>
        )}
      </div>

      <div className="mt-2 text-2xs text-fg-muted">
        Auth: <span className="font-mono">bo_session</span> cookie auto-attached.
        {lockedFields.length > 0 && (
          <>
            {' '}Field <code className="font-mono">{lockedFields.join(', ')}</code> auto-filled dari session.
          </>
        )}
      </div>
    </section>
  );
}

/**
 * Serialize structured form-mode bodyFields ke object yang siap dikirim.
 *
 * Aturan:
 *   - Drop value `undefined`, `null`, atau `''` UNTUK field yang BUKAN required.
 *     Tujuan: kurangi noise — kalau user gak isi field optional, jangan
 *     kirim key dengan value kosong yang bisa bingungin backend validation.
 *   - Untuk field required: kirim apa adanya (biar backend yang validate,
 *     biar pesan error spesifik ke field itu).
 */
/**
 * Combine schema-defined `fields` + user-added `customFields` into final
 * request body object. Custom rows berlaku saat endpoint tanpa documented
 * schema — user manual ngetik nama field.
 *
 * Aturan:
 *   - Skip schema field yang kosong DAN bukan required (kurangi noise).
 *   - Skip custom row dengan nama kosong (user belum isi).
 *   - Coerce nilai custom string → number / boolean kalau format obvious
 *     ("true"/"false" → bool, "123"/"-3.14" → number). Tetap string kalau
 *     gak match — backend yang nentuin format expect-nya.
 *   - Custom field menimpa schema field dengan nama sama (rare case, tapi
 *     user-intent: yang baru di-ketik di-prioritize).
 */
function serializeBodyFields(
  fields: Record<string, unknown>,
  schema: OpenApiSchema | null,
  customFields: CustomBodyField[]
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const required = new Set(schema?.required || []);
  for (const [k, v] of Object.entries(fields)) {
    const isEmpty = v === undefined || v === null || v === '';
    if (isEmpty && !required.has(k)) continue;
    out[k] = v;
  }
  for (const row of customFields) {
    const name = row.name.trim();
    if (!name) continue;
    out[name] = coerceScalar(row.value);
  }
  return out;
}

function coerceScalar(raw: string): unknown {
  if (raw === '') return '';
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  if (raw === 'null') return null;
  // Number coercion — only if the whole string parses as one (avoid
  // false-positive on "1abc" or phone numbers starting with 0)
  if (/^-?\d+(\.\d+)?$/.test(raw)) {
    const n = Number(raw);
    if (!Number.isNaN(n)) return n;
  }
  return raw;
}

interface ModeButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}
function ModeButton({ active, onClick, icon, label }: ModeButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 px-3 h-6 rounded-pill text-2xs font-bold transition-all',
        active
          ? 'bg-accent text-fg border-2 border-border-strong'
          : 'border-2 border-transparent text-fg-tertiary hover:text-fg'
      )}
    >
      {icon}
      {label}
    </button>
  );
}

/**
 * Recursively generate an example JSON value from an OpenAPI schema.
 *
 * Resolution order per node:
 *   1. `example` field langsung (paling akurat — author-provided)
 *   2. `enum[0]` — kalau enum, pakai value pertama
 *   3. Default berdasarkan `type`:
 *        - string: ''
 *        - number/integer: 0
 *        - boolean: false
 *        - array: 1 element generated dari `items`
 *        - object: recurse ke setiap property
 *
 * Tujuannya bukan generate request yang **valid**, tapi struktur skeleton
 * yang user tinggal isi value-nya — kayak yang dilakuin Postman / Swagger UI.
 */
function generateExampleFromSchema(schema: OpenApiSchema): unknown {
  if (schema.example !== undefined) return schema.example;
  if (schema.enum && schema.enum.length > 0) return schema.enum[0];

  switch (schema.type) {
    case 'string':
      return schema.format === 'date-time'
        ? new Date().toISOString()
        : schema.format === 'date'
        ? new Date().toISOString().slice(0, 10)
        : '';
    case 'integer':
    case 'number':
      return 0;
    case 'boolean':
      return false;
    case 'array':
      return schema.items ? [generateExampleFromSchema(schema.items)] : [];
    case 'object':
    default: {
      const out: Record<string, unknown> = {};
      if (schema.properties) {
        for (const [key, propSchema] of Object.entries(schema.properties)) {
          out[key] = generateExampleFromSchema(propSchema);
        }
      }
      return out;
    }
  }
}

function ParamGroup({
  title,
  params,
  values,
  onChange,
}: {
  title: string;
  params: OpenApiParameter[];
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
}) {
  if (params.length === 0) return null;
  return (
    <div className="px-4 py-3">
      <h3 className="text-2xs font-display font-bold uppercase tracking-wider text-fg-secondary mb-3">
        {title}
      </h3>
      <div className="space-y-2.5">
        {params.map((p) => (
          <div key={p.name} className="flex items-center gap-3">
            <label className="w-32 shrink-0 flex items-center gap-1">
              <code className="text-sm font-mono font-bold text-fg">{p.name}</code>
              {p.required && <span className="text-error">*</span>}
            </label>
            <input
              type="text"
              value={values[p.name] ?? ''}
              onChange={(e) => onChange(p.name, e.target.value)}
              placeholder={String(p.schema?.example ?? '')}
              className="flex-1 h-9 px-3 rounded-pill border-2 border-border-strong bg-base font-mono text-2xs text-fg placeholder:text-fg-tertiary focus:outline-none focus:shadow-brutal-sm transition-shadow"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
